"use client";

import { c as _c } from "react/compiler-runtime";
import { Fragment, useState, useRef, useEffect } from "react";
import Link from "next/link";
import { ChevronDownIcon, EllipsisIcon } from "lucide-react";
import type { ClassValue } from "clsx";
import ClickAwayListener from "react-click-away-listener";
import { useBreakpoint } from "react-tw-breakpoints";
import cn from "@/utils/cn";
import type { Breadcrumb } from "../types";
import useBreadcrumbs from "../hooks/use-breadcrumbs";
import { renderComponent } from "@/utils/jsx";
const smallBreakpoints = ["xs", "sm", "md"];
let wasPreviousBreakpointSmall: boolean | null = null;
export default function Breadcrumbs() {
  const [menuOpen, setMenuOpen] = useState<false | number | "ellipsis">(false);
  const [menuItemRef, setMenuItemRef] = useState<React.RefObject<HTMLLIElement>>();
  const breakpoint = useBreakpoint();
  const breadcrumbs = useBreadcrumbs();
  const length = breadcrumbs.length;
  const last = length - 1;
  const rect = menuItemRef?.current?.getBoundingClientRect();
  const isLast = (i: number) => i === last;
  const onEllipsisClick = (ref: React.RefObject<HTMLLIElement>) => {
    if (ref === menuItemRef && menuOpen === "ellipsis") return setMenuOpen(false);
    setMenuItemRef(ref);
    setMenuOpen("ellipsis");
  };
  const onClick = (ref_0: React.RefObject<HTMLLIElement>, i_0: number) => {
    if (ref_0 === menuItemRef && menuOpen === i_0) return setMenuOpen(false);
    setMenuItemRef(ref_0);
    setMenuOpen(i_0);
  };
  const onClickAway = () => menuOpen && setMenuOpen(false);
  useEffect(() => {
    if (breakpoint === "xs" && typeof menuOpen === "number") return setMenuOpen(false);
    const isCurrentBreakpointSmall = smallBreakpoints.includes(breakpoint);
    if (isCurrentBreakpointSmall === wasPreviousBreakpointSmall) return;
    if (!isCurrentBreakpointSmall && menuOpen) setMenuOpen(false);
    wasPreviousBreakpointSmall = isCurrentBreakpointSmall;
  }, [breakpoint]);
  return <ClickAwayListener onClickAway={onClickAway}>
            <nav className="h-10 border-b grow z-300">
                <ul className="inline-flex h-full text-neutral-300 pr-11 text-nowrap max-w-full overflow-hidden">
                    {breadcrumbs.map((breadcrumb, i_1) => <Fragment key={breadcrumb.dirname}>
                            <BreadcrumbItem {...breadcrumb} isFirst={!i_1} isLast={i_1 === last} className={cn(i_1 && !isLast(i_1) && "hidden lg:list-item", (i_1 === 1 || isLast(i_1 + 1)) && "sm:list-item")} onClick={breadcrumb.menu && (ref_1 => onClick(ref_1, i_1))} />

                            {i_1 === 1 && breadcrumbs.length > 2 && <BreadcrumbItem dirname={breadcrumb.dirname} icon={<EllipsisIcon className="size-[1em]" />} onlyIcon className={cn("lg:hidden", length < 5 && "sm:hidden")} onClick={onEllipsisClick} />}
                        </Fragment>)}
                </ul>

                <nav className={cn("absolute block top-full left-0 bg-neutral-900 border-x border-t z-200", menuOpen === false && "hidden", menuOpen === "ellipsis" && "lg:hidden", typeof menuOpen === "number" && "hidden sm:block")} style={{
        left: (rect?.left ?? 0) + 18,
        top: rect?.height,
        minWidth: rect?.width
      }}>   
                    <ul>
                        {menuOpen !== false && (menuOpen === "ellipsis" ? breadcrumbs.map(({
            dirname,
            href,
            icon,
            title
          }, i_2) => !!i_2 && !isLast(i_2) && <li key={dirname} className={cn("border-b list-item", (i_2 === 1 || i_2 === last - 1) && "sm:hidden")}>
                                    <Link href={href ?? "#"} className="flex items-center gap-1.5 hover:text-neutral-100 hover:bg-neutral-800 px-2.5 py-1.5">
                                        {renderComponent(icon)}{title}
                                    </Link>
                                </li>) : breadcrumbs[menuOpen].menu?.map(({
            dirname: dirname_0,
            href: href_0,
            title: title_0,
            icon: icon_0
          }) => <li key={dirname_0} className="border-b list-item">
                                    <Link href={href_0 ?? "#"} className="flex items-center gap-1.5 hover:text-neutral-100 hover:bg-neutral-800 px-2.5 py-1.5">
                                        {renderComponent(icon_0)}{title_0}
                                    </Link>
                                </li>))}
                    </ul>
                </nav>
            </nav>
        </ClickAwayListener>;
}
type BreadcrumbItemProps = Breadcrumb & {
  isFirst?: boolean;
  isLast?: boolean;
  className?: ClassValue;
  children?: React.ReactNode;
  onClick?: (ref: React.RefObject<HTMLLIElement>) => void;
};
function BreadcrumbItem(t0) {
  const $ = _c(31);
  const {
    href,
    title,
    icon,
    onlyIcon,
    isFirst,
    isLast,
    className,
    menu,
    onClick
  } = t0;
  const ref = useRef(null);
  let t1;
  if ($[0] !== onClick) {
    t1 = event => {
      event.preventDefault();
      if (ref && ref.current && onClick) {
        onClick(ref as React.RefObject<HTMLLIElement>);
      }
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClick = t1;
  const t2 = onlyIcon && "[&+li>a]:pl-10 relative";
  let t3;
  if ($[2] !== className || $[3] !== t2) {
    t3 = cn("h-full", t2, className);
    $[2] = className;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = onClick ? ref : undefined;
  const t5 = href ?? "#";
  const t6 = onClick && !menu ? handleClick : undefined;
  const t7 = !isFirst && "pl-10.5";
  const t8 = (isFirst || !isLast) && "bg-neutral-900 hover:bg-neutral-800";
  let t9;
  if ($[5] !== t7 || $[6] !== t8) {
    t9 = cn("flex items-center h-full pr-3 group hover:text-neutral-100", t7, t8);
    $[5] = t7;
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== icon) {
    t10 = renderComponent(icon);
    $[8] = icon;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = !onlyIcon && title;
  let t12;
  if ($[10] !== handleClick || $[11] !== menu) {
    t12 = !!menu && <button onClick={handleClick} className="cursor-pointer rounded p-1.5 -mr-2 hover:bg-neutral-900"><ChevronDownIcon className="size-[1em]" /></button>;
    $[10] = handleClick;
    $[11] = menu;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10 || $[14] !== t11 || $[15] !== t12) {
    t13 = <div className="z-100 flex items-center gap-1.5 pl-3">{t10}{t11}{t12}</div>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== isFirst || $[18] !== isLast) {
    t14 = (isFirst || !isLast) && <div className={cn("h-full aspect-square -m-7.5 z-90", (isFirst || !isLast) && "bg-neutral-900 group-hover:bg-neutral-800")}><i className="border-t border-r group-hover:bg-neutral-800 bg-neutral-900 block size-full rotate-45 translate-x-3" /></div>;
    $[17] = isFirst;
    $[18] = isLast;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t13 || $[21] !== t14 || $[22] !== t5 || $[23] !== t6 || $[24] !== t9 || $[25] !== title) {
    t15 = <Link href={t5} title={title} onClick={t6} className={t9}>{t13}{t14}</Link>;
    $[20] = t13;
    $[21] = t14;
    $[22] = t5;
    $[23] = t6;
    $[24] = t9;
    $[25] = title;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t15 || $[28] !== t3 || $[29] !== t4) {
    t16 = <li className={t3} ref={t4}>{t15}</li>;
    $[27] = t15;
    $[28] = t3;
    $[29] = t4;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
}